import type { MarkingPosition, TextAlignment } from '../../types'

/**
 * Расчет позиции текста в процентах от размеров canvas
 * @param pixelX - позиция X в пикселях
 * @param pixelY - позиция Y в пикселях
 * @param canvasWidth - ширина canvas
 * @param canvasHeight - высота canvas
 * @returns позиция в процентах (0-100) с точностью до 2 знаков после запятой
 */
export function calculatePositionFromPixels(
    pixelX: number,
    pixelY: number,
    canvasWidth: number,
    canvasHeight: number
): MarkingPosition {
    // Используем более точное округление для сохранения точности позиции
    // Округляем до 2 знаков после запятой для баланса между точностью и размером данных
    const x = Math.max(
        0,
        Math.min(100, Math.round((pixelX / canvasWidth) * 100 * 100) / 100)
    )
    const y = Math.max(
        0,
        Math.min(100, Math.round((pixelY / canvasHeight) * 100 * 100) / 100)
    )
    return { x, y }
}

/**
 * Расчет позиции текста в пикселях от процентов
 * @param position - позиция в процентах (0-100)
 * @param canvasWidth - ширина canvas
 * @param canvasHeight - высота canvas
 * @returns позиция в пикселях
 */
export function calculatePositionToPixels(
    position: MarkingPosition,
    canvasWidth: number,
    canvasHeight: number
): { x: number; y: number } {
    return {
        x: (position.x / 100) * canvasWidth,
        y: (position.y / 100) * canvasHeight,
    }
}

/**
 * Параметры для расчета позиции текста
 */
export interface CalculateTextPositionParams {
    /**
     * Позиция в процентах (если есть)
     */
    position?: MarkingPosition | null
    /**
     * Ширина canvas
     */
    canvasWidth: number
    /**
     * Высота canvas
     */
    canvasHeight: number
    /**
     * Сохраненная позиция X в пикселях (если есть)
     */
    savedLeft?: number
    /**
     * Сохраненная позиция Y в пикселях (если есть)
     */
    savedTop?: number
    /**
     * Выравнивание текста (для правильного расчета позиции)
     */
    textAlign?: TextAlignment
    /**
     * Ширина текстового объекта (для учета выравнивания)
     */
    textWidth?: number
    /**
     * Высота текстового объекта (для учета выравнивания)
     */
    textHeight?: number
    /**
     * Safe area для ограничения позиции текста
     */
    safeArea?: {
        top: number
        bottom: number
        left: number
        right: number
    }
}

/**
 * Расчет позиции текста в пикселях на основе сохраненной позиции или процентов
 * Важно: позиция в процентах означает позицию центра текста (с учетом выравнивания)
 * @param params - параметры для расчета позиции
 * @returns позиция { left, top } в пикселях для Fabric.js
 */
export function calculateTextPosition(params: CalculateTextPositionParams): {
    left: number
    top: number
} {
    const {
        position,
        canvasWidth,
        canvasHeight,
        savedLeft,
        savedTop,
        textAlign = 'center',
        textWidth = 0,
        textHeight = 0,
        safeArea,
    } = params

    // Используем позицию из пропсов (в процентах), если она есть
    if (position) {
        // Позиция в процентах означает позицию центра текста
        const centerX = (position.x / 100) * canvasWidth
        const centerY = (position.y / 100) * canvasHeight

        // Корректируем позицию в зависимости от выравнивания
        // В Fabric.js при textAlign: 'center' left - это центр текста
        // При textAlign: 'left' left - это левый край
        // При textAlign: 'right' left - это правый край
        //
        // ВАЖНО: position.x - это позиция центра текста, сохраненная независимо от выравнивания
        // При загрузке нужно рассчитать left в зависимости от текущего выравнивания
        let left = centerX
        if (textAlign === 'left' && textWidth > 0) {
            // Если выравнивание left, left должен быть левым краем текста
            // centerX - это центр текста, поэтому сдвигаем влево на половину ширины
            left = centerX - textWidth / 2
            // Не ограничиваем - пользователь может размещать текст где угодно
        } else if (textAlign === 'right' && textWidth > 0) {
            // Если выравнивание right, left должен быть правым краем текста
            // centerX - это центр текста, поэтому сдвигаем вправо на половину ширины
            left = centerX + textWidth / 2
            // Не ограничиваем - пользователь может размещать текст где угодно
        }
        // Для center left уже равен centerX (центр текста = центр позиции)
        // Не ограничиваем - пользователь может размещать текст где угодно

        // Для вертикального центрирования вычитаем половину высоты
        // Если textHeight = 0 (текст еще не создан), используем centerY как есть
        const top = textHeight > 0 ? centerY - textHeight / 2 : centerY
        // Не ограничиваем - пользователь может размещать текст где угодно

        // Округляем координаты до целых пикселей для четкости текста
        // Это предотвращает субпиксельный рендеринг, который вызывает размытость
        return {
            left: Math.round(left),
            top: Math.round(top),
        }
    }

    // Используем сохраненную позицию из canvas (в пикселях), если она есть
    if (savedLeft !== undefined && savedTop !== undefined) {
        // Округляем сохраненные координаты до целых пикселей
        return {
            left: Math.round(savedLeft),
            top: Math.round(savedTop),
        }
    }

    // По умолчанию центрируем
    // Округляем до целых пикселей
    return {
        left: Math.round(canvasWidth / 2),
        top: Math.round(canvasHeight / 2),
    }
}
